/**
 * ConstructPro Professional Proposal Generator
 * Creates polished PDF proposals matching CRUE Construction style
 * Supports: custom logo, company info, overhead toggle, contingency
 */

export function generateProposalHTML(estimate) {
  const { projectName, projectLocation, summary, byDivision, lineItems } = estimate;

  const totalCost = parseFloat(summary.estimateTotal);
  const laborCost = parseFloat(summary.laborCosts);
  const materialCost = parseFloat(summary.materialCosts);
  const overheadAmount = parseFloat(summary.overhead);
  const profitAmount = parseFloat(summary.profit);
  const directCosts = laborCost + materialCost;

  // Company branding
  const companyName = estimate.companyName || 'ConstructPro';
  const companyAddress = estimate.companyAddress || '';
  const companyPhone = estimate.companyPhone || '';
  const companyEmail = estimate.companyEmail || 'info@constructpro.dev';
  const companyLogo = estimate.companyLogo || null;

  // Proposal settings
  const showOverheadProfit = estimate.showOverheadProfit || false;
  const includeContingency = estimate.includeContingency !== false;
  const contingencyPercent = estimate.contingencyPercent || 0.03;
  const contingencyAmount = totalCost * contingencyPercent;

  const dateStr = new Date().toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric'
  });

  const estimateId = estimate.id || ('EST-' + Date.now());

  // Division rows
  const divisionRows = Object.entries(byDivision || {}).sort().map(([code, div]) => {
    const pct = ((div.subtotal / totalCost) * 100).toFixed(1);
    return `
      <tr>
        <td class="div-code">Div ${code}</td>
        <td class="div-name">${div.name}</td>
        <td class="center">${div.items.length}</td>
        <td class="right bold">$${div.subtotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
        <td class="right muted">${pct}%</td>
      </tr>
    `;
  }).join('');

  // Line item rows
  const lineItemRows = (lineItems || []).map((item, idx) => `
    <tr class="${idx % 2 === 0 ? 'row-even' : 'row-odd'}">
      <td class="center muted">${idx + 1}</td>
      <td class="center red bold">${item.divisionCode}</td>
      <td>${item.description}</td>
      <td class="center muted">${item.quantity}</td>
      <td class="center muted">${item.unit}</td>
      <td class="right bold">$${item.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
    </tr>
  `).join('');

  // Summary rows
  const summaryRows = showOverheadProfit ? `
    <tr class="sum-row">
      <td colspan="3" class="sum-label">Materials</td>
      <td class="sum-value">$${materialCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
    </tr>
    <tr class="sum-row">
      <td colspan="3" class="sum-label">Labor</td>
      <td class="sum-value">$${laborCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
    </tr>
    <tr class="sum-row divider">
      <td colspan="3" class="sum-label">Direct Costs</td>
      <td class="sum-value">$${directCosts.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
    </tr>
    <tr class="sum-row">
      <td colspan="3" class="sum-label">Overhead (${summary.overheadPercent || 40}%)</td>
      <td class="sum-value">$${overheadAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
    </tr>
    <tr class="sum-row">
      <td colspan="3" class="sum-label">Profit (${summary.profitPercent || 15}%)</td>
      <td class="sum-value">$${profitAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
    </tr>
  ` : '';

  const contingencyRow = includeContingency ? `
    <tr style="background:#fffbea;">
      <td colspan="3" style="padding:8px 12px; font-size:8.5pt; color:#856404; font-style:italic;">
        Recommended Owner Contingency (${(contingencyPercent * 100).toFixed(0)}%) — not included in contract sum
      </td>
      <td style="padding:8px 12px; text-align:right; font-size:8.5pt; color:#856404; font-style:italic;">
        $${contingencyAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </td>
    </tr>
  ` : '';

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>${companyName} – ${projectName}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; color: #222; font-size: 9.5pt; line-height: 1.5; background: white; }

  .header { padding: 28px 40px 20px; border-bottom: 3px solid #dc2626; display: flex; justify-content: space-between; align-items: flex-start; }
  .company-logo { max-height: 70px; max-width: 220px; object-fit: contain; display: block; margin-bottom: 6px; }
  .company-name-text { font-size: 22pt; font-weight: 900; color: #dc2626; }
  .company-details { font-size: 8pt; color: #666; line-height: 1.7; margin-top: 5px; }
  .header-right { text-align: right; }
  .est-num { font-size: 14pt; font-weight: bold; color: #222; }
  .est-date { font-size: 9pt; color: #888; margin-top: 3px; }
  .total-badge { display: inline-block; background: #dc2626; color: white; padding: 9px 18px; border-radius: 5px; margin-top: 10px; }
  .total-badge .tl { font-size: 7pt; text-transform: uppercase; opacity: 0.85; letter-spacing: 1px; }
  .total-badge .ta { font-size: 17pt; font-weight: 900; line-height: 1.2; }

  .to-bar { padding: 14px 40px; background: #f7f7f7; border-bottom: 1px solid #e0e0e0; display: flex; gap: 50px; }
  .to-field .to-lbl { font-size: 7.5pt; text-transform: uppercase; color: #aaa; font-weight: bold; margin-bottom: 3px; }
  .to-field .to-val { font-size: 10pt; color: #222; line-height: 1.5; }

  section { padding: 20px 40px; }
  section + section { border-top: 1px solid #eee; }
  .section-title { font-size: 10pt; font-weight: bold; text-transform: uppercase; letter-spacing: 1px; color: #1a1a1a; border-bottom: 2px solid #dc2626; padding-bottom: 5px; margin-bottom: 14px; }

  table { width: 100%; border-collapse: collapse; font-size: 9pt; }
  thead tr { background: #1a1a1a; color: white; }
  thead th { padding: 8px 10px; font-size: 7.5pt; text-transform: uppercase; letter-spacing: 0.4px; font-weight: 600; }
  .row-even { background: #fafafa; }
  .row-odd { background: white; }
  td { padding: 6px 10px; border-bottom: 1px solid #f0f0f0; }
  .div-code { color: #dc2626; font-weight: bold; width: 70px; }
  .div-name { font-weight: 500; }
  .center { text-align: center; }
  .right { text-align: right; }
  .bold { font-weight: bold; }
  .muted { color: #888; }
  .red { color: #dc2626; }

  .sum-row td { padding: 7px 12px; }
  .sum-label { text-align: right; color: #666; padding-right: 20px !important; }
  .sum-value { text-align: right; font-weight: 500; width: 160px; }
  .sum-row.divider td { border-top: 1px solid #ddd; font-weight: bold; color: #222; }
  .total-row td { border-top: 3px solid #dc2626 !important; font-size: 13pt; font-weight: 900; color: #dc2626; padding: 10px 12px; }

  .terms { padding: 16px 40px; border-top: 2px solid #eee; }
  .terms-title { font-size: 9pt; font-weight: bold; text-transform: uppercase; margin-bottom: 8px; color: #333; }
  .terms p { font-size: 7.5pt; color: #666; margin-bottom: 5px; }

  .footer-bar { background: #1a1a1a; color: #999; padding: 10px 40px; font-size: 7pt; display: flex; justify-content: space-between; }

  .controls { padding: 14px 20px; background: #f0f0f0; border-bottom: 1px solid #ddd; display: flex; gap: 10px; }
  .btn-print { background: #dc2626; color: white; border: none; padding: 9px 20px; border-radius: 5px; font-size: 13px; font-weight: bold; cursor: pointer; }
  .btn-close { background: #ddd; color: #333; border: none; padding: 9px 20px; border-radius: 5px; font-size: 13px; cursor: pointer; }

  @media print {
    .controls { display: none !important; }
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    section { page-break-inside: avoid; }
    .page-break { page-break-before: always; }
  }
</style>
</head>
<body>

<div class="controls no-print">
  <button class="btn-print" onclick="window.print()">Print / Save as PDF</button>
  <button class="btn-close" onclick="window.close()">Close</button>
</div>

<!-- Header -->
<div class="header">
  <div>
    ${companyLogo
      ? `<img src="${companyLogo}" class="company-logo" alt="${companyName}" />`
      : `<div class="company-name-text">${companyName}</div>`
    }
    <div class="company-details">
      ${companyAddress ? companyAddress + '<br>' : ''}
      ${companyPhone ? companyPhone + (companyEmail ? ' | ' : '') : ''}
      ${companyEmail}
    </div>
  </div>
  <div class="header-right">
    <div class="est-num">${estimateId}</div>
    <div class="est-date">DATE: ${dateStr}</div>
    <div class="total-badge">
      <div class="tl">TOTAL</div>
      <div class="ta">USD $${totalCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
    </div>
  </div>
</div>

<!-- To Bar -->
<div class="to-bar">
  <div class="to-field">
    <div class="to-lbl">To</div>
    <div class="to-val"><strong>${projectName}</strong><br>${estimate.projectAddress || projectLocation || ''}</div>
  </div>
  <div class="to-field">
    <div class="to-lbl">Location</div>
    <div class="to-val">${projectLocation}</div>
  </div>
  <div class="to-field">
    <div class="to-lbl">Estimate Date</div>
    <div class="to-val">${dateStr}</div>
  </div>
  <div class="to-field">
    <div class="to-lbl">Valid For</div>
    <div class="to-val">30 Days</div>
  </div>
</div>

<!-- Scope by Division -->
<section>
  <div class="section-title">Scope of Work by CSI Division</div>
  <table>
    <thead>
      <tr>
        <th style="width:70px;">Division</th>
        <th>Description</th>
        <th class="center" style="width:60px;">Items</th>
        <th class="right" style="width:150px;">Subtotal</th>
        <th class="right" style="width:70px;">%</th>
      </tr>
    </thead>
    <tbody>
      ${divisionRows}
      ${summaryRows}
      <tr class="total-row">
        <td colspan="3" style="text-align:right; padding-right:20px;">TOTAL</td>
        <td colspan="2" style="text-align:right;">USD $${totalCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
      </tr>
      ${contingencyRow}
    </tbody>
  </table>
</section>

<!-- Line Items -->
<section class="page-break">
  <div class="section-title">Detailed Line Items (${lineItems ? lineItems.length : 0} Items)</div>
  <table>
    <thead>
      <tr>
        <th class="center" style="width:35px;">#</th>
        <th class="center" style="width:45px;">Div</th>
        <th>Description</th>
        <th class="center" style="width:55px;">Qty</th>
        <th class="center" style="width:55px;">Unit</th>
        <th class="right" style="width:110px;">Total</th>
      </tr>
    </thead>
    <tbody>
      ${lineItemRows}
    </tbody>
  </table>
</section>

<!-- Terms -->
<div class="terms">
  <div class="terms-title">Terms & Conditions</div>
  <p><strong>Permitting:</strong> Permit fees and agency review costs are not included unless specifically listed. Approvals may take 2–5 months.</p>
  <p><strong>Unforeseen Conditions:</strong> Pricing assumes typical site conditions. Buried obstructions, contaminated materials, or groundwater complications require a change order.</p>
  <p><strong>Access:</strong> Owner shall provide equipment access, staging area, and delivery access for materials and hauling operations.</p>
  <p><strong>Material Quantities:</strong> All quantities are estimated. Additional materials beyond estimated amounts will be billed accordingly.</p>
  <p><strong>Change Orders:</strong> Any scope changes require written approval prior to proceeding.</p>
  <p><strong>Schedule:</strong> Subject to permitting approvals and site conditions.</p>
  ${includeContingency ? `<p><strong>Owner Contingency:</strong> It is recommended that the Owner carry a contingency of $${contingencyAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (${(contingencyPercent * 100).toFixed(0)}%) separate from the Contract Sum for unforeseen changes.</p>` : ''}
</div>

<!-- Footer -->
<div class="footer-bar">
  <div>This estimate is based on extracted scope from construction documents. Final pricing subject to field conditions and client approval.</div>
  <div>Generated by ConstructPro | constructpro.dev</div>
</div>

</body>
</html>`;
}

export function openProposalWindow(estimate) {
  const html = generateProposalHTML(estimate);
  const newWindow = window.open('', '_blank');
  if (newWindow) {
    newWindow.document.write(html);
    newWindow.document.close();
  }
}

export function downloadProposal(estimate, filename = 'estimate.html') {
  const html = generateProposalHTML(estimate);
  const blob = new Blob([html], { type: 'text/html' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}
